/**
 * Cost-cap handler — one exhaustion experience across both transports.
 *
 * The anonymous demo can run out of credits two ways, and the server
 * reports them differently because the transports differ:
 *
 *   - REST turns get an HTTP status (429 / 402).
 *   - The stage WebSocket has no status line mid-stream, so it sends a
 *     `server.error` frame with `fatal: true` and an `error_code`.
 *
 * Both funnel through `applyCapFallback`, so a visitor sees the same
 * banner regardless of which surface noticed first.
 *
 * The `error_code` vocabulary is the server's, taken verbatim from
 * `apps/agent-system/animachord/api/rehearsal/handlers.py`
 * (`credits_exhausted_error`) and the socket mirror in
 * `apps/agent-system/animachord/api/stage_ws/gateway.py`
 * (`_credits_error`). Both paths emit exactly two codes.
 */

import type { StageMessage } from '@/api';
import type { VoiceFallbackKind, VoiceStoreSlice } from '@/voice/types';

/** Server `error_code` for a session that has spent its own grant. */
export const ERROR_CODE_CREDITS_EXHAUSTED = 'credits_exhausted';
/** Server `error_code` for the shared daily ceiling — nobody gets a turn. */
export const ERROR_CODE_DEMO_AT_CAPACITY = 'demo_at_capacity';

/** The fallback kinds this module owns. */
type CapKind = Extract<
  VoiceFallbackKind,
  'cost-cap-anon' | 'cost-cap-402' | 'demo-at-capacity'
>;

interface CapCopy {
  readonly message: string;
  /**
   * PLACEHOLDER — `cost-cap-anon` carries a sign-in label but no
   * `ctaAction`, so no button renders today. Pre-dates this module; kept
   * byte-identical so the socket and HTTP paths stay in lockstep. Wiring
   * it needs a product answer (does signing in actually grant more demo
   * credits?) that does not exist yet — do not invent one here.
   */
  readonly ctaLabel?: string;
}

/**
 * Single source of banner copy. Written for an anonymous visitor trying a
 * demo: state the fact, name no price or plan, blame nobody.
 */
const CAP_COPY: Record<CapKind, CapCopy> = {
  'cost-cap-anon': {
    message: "You've used the free credits for this demo session.",
    // No CTA, deliberately. This carried `ctaLabel: 'Save & sign in'` with no
    // action wired, so it rendered nothing; the decision (2026-08-10) is to
    // remove it rather than wire it.
    //
    // Signing in would not help. The rehearsal credit scopes are per-session
    // and global-daily, so authenticating refills neither, and the demo is
    // reached by a shared link with no signup flow behind it. A button that
    // does nothing useful is worse than no button, and a label sitting here
    // unwired invites someone to connect it later on the assumption that it
    // was meant to work.
    //
    // If a paid tier ever lands on this surface, add the CTA then, together
    // with the thing it actually buys.
  },
  'cost-cap-402': {
    message: 'Voice usage limit reached for this billing period.',
  },
  'demo-at-capacity': {
    message: 'The demo is busy right now. It frees up after midnight UTC.',
  },
};

/**
 * Put the store into the shared exhaustion state. Both the HTTP and the
 * socket path end here, which is what keeps them from drifting.
 */
export function applyCapFallback(kind: CapKind, store: VoiceStoreSlice): CapKind {
  const copy = CAP_COPY[kind];
  store.setVoiceMode('fallen-back');
  store.setActiveBanner({
    kind,
    message: copy.message,
    ctaLabel: copy.ctaLabel,
    ctaAction: undefined,
  });
  return kind;
}

/**
 * Call when a voice API response arrives.
 *
 * - 429 → `'cost-cap-anon'`, or `'demo-at-capacity'` when the body's
 *   `error_code` says the ceiling is global rather than this visitor's.
 *   The server returns 429 for both (see `credits_exhausted_error`), so
 *   the status alone cannot tell them apart.
 * - 402 → `'cost-cap-402'` (authenticated session over limit)
 * - all other statuses → `null` (no cost-cap action needed)
 *
 * Returns the `VoiceFallbackKind` if a cost-cap was applied, `null` otherwise.
 */
export function handleCapResponse(
  status: number,
  store: VoiceStoreSlice,
  errorCode?: string,
): VoiceFallbackKind | null {
  if (status === 429) {
    return applyCapFallback(
      errorCode === ERROR_CODE_DEMO_AT_CAPACITY ? 'demo-at-capacity' : 'cost-cap-anon',
      store,
    );
  }
  if (status === 402) {
    return applyCapFallback('cost-cap-402', store);
  }
  return null;
}

/**
 * Call for every inbound stage frame.
 *
 * Only a `server.error` carrying one of the two exhaustion codes produces
 * a banner. Every other frame — including non-cap `server.error`s such as
 * `not_found`, `forbidden`, or `internal_error` — returns `null` and
 * leaves the store untouched, because showing "you're out of credits" for
 * an unrelated failure is worse than showing nothing.
 *
 * Terminating the socket is not this function's job; `StageSocketClient`
 * latches `fatal: true` itself so the two concerns stay separable.
 */
export function handleCapFrame(
  frame: StageMessage,
  store: VoiceStoreSlice,
): VoiceFallbackKind | null {
  if (frame.kind !== 'server.error') return null;
  switch (frame.error.error) {
    case ERROR_CODE_CREDITS_EXHAUSTED:
      return applyCapFallback('cost-cap-anon', store);
    case ERROR_CODE_DEMO_AT_CAPACITY:
      return applyCapFallback('demo-at-capacity', store);
    default:
      return null;
  }
}
